import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabaseClient } from '../../config/supabase';
import ManualDriverActivation from './ManualDriverActivation';

function truncatedId(id) {
    return id ? `${id.slice(0, 8)}…` : '—';
}

function driverPrimaryLabel(driver) {
    const profileName = driver?.user_profile?.full_name?.trim();
    return profileName || truncatedId(driver?.id);
}

// Module 11 — Affiliate Roster View (Gate 1: Admin Dispatch UI, BL-168).
//
// Shows 1099_affiliate drivers only.
// Field visibility: Affiliate-thinnest tier per Module 11's four-tier matrix.
// Dispatch-full: id, classification, status, reliability_score, default_vehicle
// Affiliate-thinnest: id, classification, status (no scores, no terms, no internal notes)
function DriversView({ userId, theme = 'light', isUser = false, embedded = false }) {
    const dark = theme === 'dark';

    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const loadData = useCallback(async () => {
        setLoadError(null);
        const dRes = await supabaseClient.rpc('bl395a_dispatch_driver_directory');
        if (dRes.error) {
            setLoadError(dRes.error.message);
            setDrivers([]);
        } else {
            const loadedDrivers = (dRes.data || [])
                .filter((driver) => driver.employment_classification === '1099_affiliate')
                .map((driver) => ({
                    ...driver,
                    id: driver.driver_id,
                    user_profile: { full_name: driver.display_name },
                }));
            setDrivers(loadedDrivers);
            setVehicles(loadedDrivers
                .filter((driver) => driver.default_vehicle_id)
                .map((driver) => ({
                    id: driver.default_vehicle_id,
                    driver_id: driver.id,
                    label: driver.vehicle_label,
                    plate: driver.vehicle_plate,
                    vehicle_class: driver.vehicle_class,
                    capacity: driver.vehicle_capacity,
                })));
        }
        setLoading(false);
    }, []);

    useEffect(() => { loadData(); }, [loadData, userId]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return drivers.filter(d => {
            if (statusFilter !== 'all' && d.status !== statusFilter) return false;
            if (!q) return true;
            return d.id.toLowerCase().includes(q)
                || (d.user_profile?.full_name || '').toLowerCase().includes(q)
                || (d.status || '').toLowerCase().includes(q);
        });
    }, [drivers, statusFilter, search]);

    const cardCls = 'bg-[var(--surface-raised)] border-[var(--surface-border)]';
    const headCls = dark ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-200';
    const rowCls = dark ? 'border-gray-700 hover:bg-gray-700/40' : 'border-gray-100 hover:bg-[var(--accent-ink)]/10';
    const controlCls = 'rounded-lg border border-[var(--surface-border)] bg-[var(--surface-raised)] px-3 py-2 text-sm text-[var(--ink)]';

    return (
        <div className="ia-dispatch h-full flex flex-col">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    {embedded
                        ? <h2 className="text-xl font-bold text-[var(--ink)]">🤝 Affiliates</h2>
                        : <h1 className={`text-2xl font-bold ${'text-[var(--ink)]'}`}>🤝 Affiliate Roster</h1>}
                    <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                        1099 affiliate drivers available for farm-out assignments.
                    </p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
                <input
                    type="text"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    placeholder="Search by driver, ID, or status…"
                    className={`${controlCls} flex-1 min-w-[200px]`}
                />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={controlCls}>
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                </select>
                <span className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{filtered.length} shown</span>
            </div>

            <div className={`flex-1 overflow-auto rounded-xl border ${cardCls}`}>
                {loading ? (
                    <div className="p-10 text-center text-gray-500">Loading affiliate roster…</div>
                ) : loadError ? (
                    <div className="p-6 text-center text-red-600">Could not load affiliates: {loadError}</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="text-4xl mb-2">🤝</div>
                        <div className={`font-medium ${dark ? 'text-gray-200' : 'text-gray-700'}`}>No affiliates on roster</div>
                        <div className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>1099 affiliate drivers added via onboarding will appear here.</div>
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className={`text-left border-b uppercase text-xs tracking-wide ${headCls}`}>
                                <th className="px-4 py-3">Driver</th>
                                <th className="px-4 py-3">Classification</th>
                                <th className="px-4 py-3">Roster Status</th>
                                <th className="px-4 py-3">Default Vehicle</th>
                                {!isUser && <th className="px-4 py-3">Added</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(d => {
                                const dv = vehicles.find(v => v.id === d.default_vehicle_id);
                                return (
                                    <tr key={d.id} className={`border-b ${rowCls}`}>
                                        <td className={`px-4 py-3 ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
                                            <div className="text-sm font-medium">{driverPrimaryLabel(d)}</div>
                                        </td>
                                        <td className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>1099 Affiliate</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${d.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                                {d.status || '—'}
                                            </span>
                                            {!isUser && !d.has_app_login && <span className="block mt-1 text-xs text-amber-700">No app login yet</span>}
                                            {!isUser && <ManualDriverActivation driver={d} onActivated={loadData} />}
                                        </td>
                                        <td className={`px-4 py-3 ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {dv ? `${dv.label || dv.vehicle_class || '—'} ${dv.plate ? `(${dv.plate})` : ''}`.trim() : '—'}
                                        </td>
                                        {!isUser && <td className={`px-4 py-3 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {d.created_at ? new Date(d.created_at).toLocaleDateString() : '—'}
                                        </td>}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default DriversView;
